<template>
    <Teleport to="body">
        <ul
            v-if="show"
            ref="menuRef"
            v-on-click-outside="handleClickOutside"
            class="ui-context-menu"
            :class="[`ui-context-menu--level-${level}`]"
            :style="menuStyle"
            :data-level="level"
            role="menu"
            @keydown.esc="close"
            @keydown="handleKeyDown"
        >
            <!-- Slot-based items (for maximum flexibility) -->
            <slot :close="close"></slot>

            <!-- Config-based items (for simplicity) -->
            <ContextMenuItem
                v-for="(item, index) in visibleItems"
                :key="index"
                :item="item"
                :index="index"
                :level="level"
                :max-depth="maxDepth"
                @close-all="close"
            />
        </ul>
    </Teleport>
</template>

<script setup lang="ts">
import { computed, ref, watch, nextTick, provide } from 'vue';
import { vOnClickOutside } from '@vueuse/components';
import { onKeyStroke } from '@vueuse/core';
import ContextMenuItem from './ContextMenuItem.vue';
import {
    useContextMenuNavigation,
    type NavigationState,
    type HoverState,
    type FlatMenuItem,
} from './useContextMenuNavigation';

/**
 * Context menu item configuration
 */
export interface ContextMenuItem {
    label: string; // Menu item text (or translation key)
    icon?: string; // Hero icon path (e.g., 'hero/outline/pencil')
    action?: () => void; // Click handler
    show?: boolean | (() => boolean); // Conditional visibility
    danger?: boolean; // Danger styling (red hover)
    class?: string; // Additional CSS classes
    children?: ContextMenuItem[]; // Nested submenu items
    disabled?: boolean; // Disabled state
}

interface Props {
    items?: ContextMenuItem[]; // Menu items configuration
    x: number; // X position (from event.clientX)
    y: number; // Y position (from event.clientY)
    show: boolean; // Visibility flag
    offsetX?: number; // X offset from cursor (default: 2)
    offsetY?: number; // Y offset from cursor (default: 10)
    level?: number; // Nesting level (0 = root)
    maxDepth?: number; // Maximum nesting depth (default: 5)
    zIndex?: number; // Stacking base — raise above host chrome (default: 50)
}

const props = withDefaults(defineProps<Props>(), {
    items: () => [],
    offsetX: 2,
    offsetY: 10,
    level: 0,
    maxDepth: 5,
    zIndex: 50,
});

const emit = defineEmits<{
    (event: 'close'): void;
    (event: 'item-click', item: ContextMenuItem): void;
}>();

const menuRef = ref<HTMLElement | null>(null);
const adjustedPosition = ref({ x: 0, y: 0 });
const focusedItemId = ref<string | null>(null);

// Navigation state for data-driven navigation
const navigationState = ref<NavigationState>({
    flatIndex: new Map(),
    rootIds: [],
    childrenMap: new Map(),
    focusedId: null,
    openSubmenus: new Set(),
});

// Hover state separate from keyboard navigation
const hoverState = ref<HoverState>({
    hoveredPath: [],
    activeTimers: new Map(),
});

// Provide legacy focus state for backward compatibility
provide('focusedItemId', focusedItemId);
provide('setFocusedItemId', (id: string) => {
    focusedItemId.value = id;
    navigationState.value.focusedId = id;
});

// Provide navigation state
provide('navigationState', navigationState);
provide('hoverState', hoverState);
provide('contextMenuZIndex', props.zIndex);
provide('toggleSubmenu', (id: string, open: boolean) => {
    if (open) {
        // Close all sibling submenus at the same level
        const item = navigationState.value.flatIndex.get(id);
        if (item) {
            const parentId = item.parentId;
            const siblings = parentId
                ? navigationState.value.childrenMap.get(parentId) || []
                : navigationState.value.rootIds;

            siblings.forEach((siblingId) => {
                if (siblingId !== id && navigationState.value.openSubmenus.has(siblingId)) {
                    navigationState.value.openSubmenus.delete(siblingId);
                    // Also close all descendants of this sibling
                    const closeDescendants = (closingId: string) => {
                        const children = navigationState.value.childrenMap.get(closingId) || [];
                        children.forEach((childId) => {
                            navigationState.value.openSubmenus.delete(childId);
                            closeDescendants(childId);
                        });
                    };
                    closeDescendants(siblingId);
                }
            });
        }

        navigationState.value.openSubmenus.add(id);
    } else {
        navigationState.value.openSubmenus.delete(id);
        // Also close all descendants
        const closeDescendants = (closingId: string) => {
            const children = navigationState.value.childrenMap.get(closingId) || [];
            children.forEach((childId) => {
                navigationState.value.openSubmenus.delete(childId);
                closeDescendants(childId);
            });
        };
        closeDescendants(id);
    }
});

// Navigation composable
const navigation = useContextMenuNavigation(navigationState);

/**
 * Filter items based on show condition
 */
const visibleItems = computed(() => {
    return props.items.filter((item) => {
        if (item.show === undefined) return true;
        if (typeof item.show === 'boolean') return item.show;
        if (typeof item.show === 'function') return item.show();
        return true;
    });
});

/**
 * Build flat navigation index from tree structure
 */
const buildNavigationIndex = () => {
    const flatIndex = new Map<string, FlatMenuItem>();
    const childrenMap = new Map<string, string[]>();
    const rootIds: string[] = [];

    const processItem = (
        item: ContextMenuItem,
        parentId: string | null,
        indexInParent: number,
        level: number,
        path: number[],
    ): string => {
        const id = parentId ? `${parentId}-${indexInParent}` : `${indexInParent}`;

        const flatItem: FlatMenuItem = {
            id,
            item,
            level,
            parentId,
            indexInParent,
            path,
            isDisabled: item.disabled || false,
            hasChildren: Boolean(item.children?.length),
        };

        flatIndex.set(id, flatItem);

        // Track parent-child relationships
        if (parentId === null) {
            rootIds.push(id);
        } else {
            if (!childrenMap.has(parentId)) {
                childrenMap.set(parentId, []);
            }
            childrenMap.get(parentId)!.push(id);
        }

        // Recursively process children
        if (item.children?.length) {
            item.children.forEach((child, childIndex) => {
                processItem(child, id, childIndex, level + 1, [...path, childIndex]);
            });
        }

        return id;
    };

    // Process visible items only
    visibleItems.value.forEach((item, index) => {
        processItem(item, null, index, 0, [index]);
    });

    navigationState.value.flatIndex = flatIndex;
    navigationState.value.rootIds = rootIds;
    navigationState.value.childrenMap = childrenMap;
};

// Build navigation index when items change
watch(
    visibleItems,
    () => {
        buildNavigationIndex();
    },
    { immediate: true },
);

/**
 * Calculate menu position with viewport boundary detection
 */
const menuStyle = computed(() => {
    return {
        top: `${adjustedPosition.value.y}px`,
        left: `${adjustedPosition.value.x}px`,
        zIndex: props.zIndex,
    };
});

/**
 * Adjust position to prevent overflow
 */
const calculatePosition = async () => {
    // Initial position with offsets
    let x = props.x + props.offsetX;
    let y = props.y + props.offsetY;

    // Wait for menu to render to get dimensions
    await nextTick();

    if (menuRef.value) {
        const menuRect = menuRef.value.getBoundingClientRect();
        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;

        // Adjust horizontal position if menu overflows right edge
        if (x + menuRect.width > viewportWidth) {
            x = viewportWidth - menuRect.width - 8; // 8px padding from edge
        }

        // Adjust vertical position if menu overflows bottom edge
        if (y + menuRect.height > viewportHeight) {
            y = viewportHeight - menuRect.height - 8; // 8px padding from edge
        }

        // Ensure menu doesn't go off left/top edges
        x = Math.max(8, x);
        y = Math.max(8, y);
    }

    adjustedPosition.value = { x, y };
};

/**
 * Recalculate position when menu opens or position changes
 */
watch(
    () => [props.show, props.x, props.y],
    async () => {
        if (props.show) {
            await calculatePosition();
            await nextTick();
            // Wait additional tick for DOM to fully stabilize
            await nextTick();

            // Focus first item using navigation state
            const firstId = navigationState.value.rootIds[0];
            if (firstId) {
                setTimeout(() => {
                    const success = navigation.focusItemById(firstId);
                    if (success) {
                        navigationState.value.focusedId = firstId;
                        focusedItemId.value = firstId;
                    }
                }, 50);
            }
        } else {
            // Reset state when menu closes
            focusedItemId.value = null;
            navigationState.value.focusedId = null;
            navigationState.value.openSubmenus.clear();
            hoverState.value.hoveredPath = [];
            hoverState.value.activeTimers.forEach((timerId) => clearTimeout(timerId));
            hoverState.value.activeTimers.clear();
        }
    },
    { immediate: true },
);

/**
 * Close menu
 */
const close = () => {
    emit('close');
};

/**
 * Handle click outside
 */
const handleClickOutside = () => {
    close();
};

/**
 * Handle keyboard navigation at menu level
 */
const handleKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'Tab') {
        event.preventDefault();
        const focusableItems = Array.from(
            menuRef.value?.querySelectorAll('.ui-context-menu-item:not(.ui-context-menu-item--disabled)') || [],
        ) as HTMLElement[];

        const currentIndex = focusableItems.indexOf(document.activeElement as HTMLElement);

        if (event.shiftKey) {
            // Shift+Tab: previous item or wrap to last
            const prevIndex = currentIndex > 0 ? currentIndex - 1 : focusableItems.length - 1;
            focusableItems[prevIndex]?.focus();
        } else {
            // Tab: next item or wrap to first
            const nextIndex = currentIndex < focusableItems.length - 1 ? currentIndex + 1 : 0;
            focusableItems[nextIndex]?.focus();
        }
    }
};

/**
 * Global Escape key handler
 */
onKeyStroke('Escape', (e) => {
    if (props.show) {
        e.preventDefault();
        close();
    }
});
</script>
